import { Link } from 'react-router-dom';
import type { Agent } from './api';

// parseTrigger derives the human-friendly meaning of agent_runs.triggered_by.
// The string is opaque to the user (e.g. 'a2a:ac_live:550e8400-…'); we
// split the prefix off and surface the relevant id so the row can link
// to the calling agent, scheduled task, or webhook delivery.
type Trigger =
  | { kind: 'manual'; userId: string }
  | { kind: 'cron'; taskId: string }
  | { kind: 'webhook'; eventId: string }
  | { kind: 'agent'; callerAgentId: string }
  | { kind: 'api'; keyPrefix: string }
  | { kind: 'unknown'; raw: string };

export function parseTrigger(raw: string | undefined | null): Trigger {
  if (!raw) return { kind: 'unknown', raw: '' };
  if (raw.startsWith('manual:')) return { kind: 'manual', userId: raw.slice(7) };
  if (raw.startsWith('cron:')) return { kind: 'cron', taskId: raw.slice(5) };
  if (raw.startsWith('webhook:')) return { kind: 'webhook', eventId: raw.slice(8) };
  if (raw.startsWith('a2a:ac_live:')) {
    return { kind: 'agent', callerAgentId: raw.slice('a2a:ac_live:'.length) };
  }
  if (raw.startsWith('a2a:sk_live:')) {
    return { kind: 'api', keyPrefix: raw.slice('a2a:sk_live:'.length) };
  }
  return { kind: 'unknown', raw };
}

// TriggerBadge renders a small pill that explains how the run was fired
// and (for agent-to-agent calls) who called it. `agentNames` maps agent
// UUID → display name so we can show 'Agent · Trading Bot' instead of
// the bare UUID.
export function TriggerBadge({
  raw,
  agentNames,
}: {
  raw: string | undefined | null;
  agentNames?: Record<string, string>;
}) {
  const t = parseTrigger(raw);
  switch (t.kind) {
    case 'manual':
      return <Pill tone="green">Manual</Pill>;
    case 'cron':
      return <Pill tone="blue" title={t.taskId}>Cron</Pill>;
    case 'webhook':
      return <Pill tone="purple" title={`event ${t.eventId}`}>Webhook</Pill>;
    case 'agent': {
      const name = agentNames?.[t.callerAgentId] ?? shortId(t.callerAgentId);
      return (
        <Link
          to={`/agents/${t.callerAgentId}`}
          className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-mono text-[10px] uppercase text-indigo-700 hover:border-indigo-400"
          title={`called by ${t.callerAgentId}`}
        >
          <span>Agent</span>
          <span className="text-indigo-500">·</span>
          <span className="normal-case">{name}</span>
        </Link>
      );
    }
    case 'api':
      return <Pill tone="gray" title={t.keyPrefix}>API · {t.keyPrefix}</Pill>;
    default:
      return <Pill tone="gray">{t.raw || '—'}</Pill>;
  }
}

type Tone = 'green' | 'blue' | 'purple' | 'gray';

function Pill({
  children,
  tone,
  title,
}: {
  children: React.ReactNode;
  tone: Tone;
  title?: string;
}) {
  const cls = {
    green: 'border-ok/30 bg-ok/10 text-ok',
    blue: 'border-bit2me/30 bg-bit2me/10 text-bit2me',
    purple: 'border-purple-200 bg-purple-50 text-purple-700',
    gray: 'border-line bg-surface-soft text-ink-tertiary',
  }[tone];
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${cls}`}
    >
      {children}
    </span>
  );
}

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return id.slice(0, 6) + '…' + id.slice(-4);
}

// agentNameMap builds the UUID→name lookup the TriggerBadge needs from
// a list of agents. Kept here so callers don't duplicate the .reduce.
export function agentNameMap(list: Agent[] | undefined): Record<string, string> {
  if (!list) return {};
  const out: Record<string, string> = {};
  for (const a of list) out[a.id] = a.name;
  return out;
}
